import { Injectable, NgModule } from '@angular/core';
import CustomDnsBinding from 'mbs-ui-shared@models/CustomDnsBinding';
import CustomDnsCertificate from 'mbs-ui-shared@models/CustomDnsCertificate';
import { Actions, EffectsModule, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, ReducerManager, Store, createAction, createSelector, on, props } from '@ngrx/store';
import { CustomDnsBindingsService } from 'mbs-ui-shared@services/custom-dns-bindings.service';
import { CustomDnsCertificatesService } from 'mbs-ui-shared@services/custom-dns-certificates.service';
import { StoreTypes } from '../storeTypes.enum';
import { isNil } from 'lodash';
import { Observable, catchError, filter, map, mergeMap, of } from 'rxjs';
import { PagingStoreFactory } from '../pagingStoreFactory/pagingStoreFactory';
import { PagingActions, PagingSelectors } from '../pagingStoreFactory/pagingStoreTypes';
import {
  CustomDnsBindingStoreProperties,
  DnsBindingCustomActionTypes,
  DnsBindingCustomSelectorTypes,
  DnsBindingStoreCustomActionsType,
  DnsBindingStoreCustomActionsTypes
} from './customDnsBindingStore.types';
import { PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models';

@Injectable({
  providedIn: 'root'
})
export class CustomDnsBindingStore extends PagingStoreFactory<CustomDnsBinding, CustomDnsBindingStoreProperties> {
  protected storeType = StoreTypes.CustomDnsBinding;
  protected selectId = (entity: CustomDnsBinding): string => {
    return entity?.id;
  };

  protected getEntityById(id: string): Observable<CustomDnsBinding> {
    return this.customDnsBindingService.getById(id);
  }
  protected getEntities(params: QueryFiltersParams & QueryPagingParams): Observable<PagedResponse<CustomDnsBinding>> {
    return this.customDnsBindingService.get(params);
  }

  constructor(
    public actions$: Actions,
    public store: Store,
    public reducerManager: ReducerManager,
    private customDnsBindingService: CustomDnsBindingsService,
    private certificatesService: CustomDnsCertificatesService
  ) {
    super(actions$, store, reducerManager);
    this.initStore();
  }

  // custom section
  // custom initial state
  protected getCustomInitialState() {
    return {
      custom: {
        certificates: [],
        loadedCertificates: false,
        loadingCertificates: false
      } as CustomDnsBindingStoreProperties
    };
  }

  // custom types
  public actions: PagingActions<CustomDnsBinding> & DnsBindingCustomActionTypes;
  public selectors: PagingSelectors<CustomDnsBinding, CustomDnsBindingStoreProperties> & DnsBindingCustomSelectorTypes;

  private getCustomActionType = (actionType: DnsBindingStoreCustomActionsTypes): DnsBindingStoreCustomActionsType =>
    `[${this.storeType}] ${actionType}`;

  // custom actions
  protected prepareCustomActions(): void {
    this.actions.loadCertificates = createAction(
      this.getCustomActionType(DnsBindingStoreCustomActionsTypes.loadCertificates),
      props<{ force?: boolean }>()
    );
    this.actions.setCertificates = createAction(
      this.getCustomActionType(DnsBindingStoreCustomActionsTypes.setCertificates),
      props<{ certificates: CustomDnsCertificate[] }>()
    );
  }

  // custom selectors
  protected prepareCustomSelectors(): void {
    this.selectors.selectCertificates = createSelector(this.selectors.featureSelector, (state) => state.custom?.certificates ?? []);
    this.selectors.selectLoadedCertificates = createSelector(
      this.selectors.featureSelector,
      (state) => state.custom?.loadedCertificates ?? false
    );
    this.selectors.selectLoadingCertificates = createSelector(
      this.selectors.featureSelector,
      (state) => state.custom?.loadingCertificates ?? false
    );
  }

  // custom reducer
  protected prepareCustomReducer(): void {
    this.customReducer = [
      on(this.actions.loadCertificates, (state, action) => {
        (action.force || !state.custom.loadedCertificates) && (state.custom.loadingCertificates = true);

        return state;
      }),
      on(this.actions.setCertificates, (state, action) => {
        state.custom.certificates = action.certificates;
        state.custom.loadedCertificates = !isNil(action.certificates);
        state.custom.loadingCertificates = false;

        return state;
      })
    ];
  }

  // custom effects
  public loadCertificates$: Observable<Action>;

  protected prepareCustomEffects(): void {
    this.loadCertificates$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(this.actions.loadCertificates),
        concatLatestFrom(() => this.store.select(this.selectors.selectLoadedCertificates)),
        filter(([action, loaded]) => action.force || !loaded),
        mergeMap(() => {
          return this.certificatesService.get().pipe(
            map((result) => this.actions.setCertificates({ certificates: result?.data })),
            catchError(() => of(this.actions.setCertificates({ certificates: null })))
          );
        })
      );
    });
  }
}

@NgModule({
  imports: [EffectsModule.forFeature([CustomDnsBindingStore])]
})
export class CustomDnsBindingStoreModule {}
